import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { User, Mail, Camera, Save, LogOut, Shield, Music, ListMusic, FolderOpen, LifeBuoy, LayoutGrid, RotateCcw, Copy, Trash2, AlertTriangle, CheckCircle2, Loader2, HelpCircle, Radio, Volume2, Image as ImageIcon, Sparkles, XCircle, Download, Monitor, Apple, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import CoachPrefsCard from '@/components/CoachPrefsCard';
import { useDashboardWidgets } from '@/utils/dashboardWidgets';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout, updateProfile, isAdmin } = useAuth();
  const [name, setName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    tracks: 0,
    playlists: 0,
    crates: 0
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    setName(user.name || '');
    setAvatarUrl(user.avatar_url || '');
    fetchStats();
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      const [tracksRes, playlistsRes, cratesRes] = await Promise.all([
        axios.get(`${API}/tracks`),
        axios.get(`${API}/playlists`),
        axios.get(`${API}/crates`)
      ]);
      setStats({
        tracks: tracksRes.data.tracks?.length || 0,
        playlists: playlistsRes.data.playlists?.length || 0,
        crates: cratesRes.data.crates?.length || 0
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateProfile({ name, avatar_url: avatarUrl || null });
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out');
    navigate('/');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 data-page-header="redundant" className="font-heading text-3xl sm:text-4xl font-bold text-[#0F172A] mb-8 tracking-tight">
            Your <span className="gradient-text">profile</span>
          </h1>

          {/* Profile Card */}
          <div className="glass-pane p-6 sm:p-8 mb-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-[#EFF6FF] to-[#ECFEFF] border border-[#DBEAFE] flex items-center justify-center overflow-hidden shadow-[0_4px_12px_rgba(37,99,255,0.12)]">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={name} className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-12 h-12 text-[#1E3A8A]" />
                  )}
                </div>
                <button className="absolute -bottom-2 -right-2 w-8 h-8 bg-[#2563FF] flex items-center justify-center hover:bg-[#3B82F6] transition-colors">
                  <Camera className="w-4 h-4 text-[#0F172A]" />
                </button>
              </div>

              {/* Info */}
              <div className="flex-1 text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start gap-3 mb-2">
                  <h2 className="font-heading text-xl sm:text-2xl text-[#0F172A]">{user.name}</h2>
                  {isAdmin && (
                    <span className="badge-data bg-[#EFF6FF] text-[#2563FF] border-[#E2E8F0] flex items-center gap-1">
                      <Shield className="w-3 h-3" />
                      Admin
                    </span>
                  )}
                </div>
                <p className="font-mono text-sm text-[#64748B]">{user.email}</p>
              </div>
            </div>

            {/* Stats */}
            {!isAdmin && (
              <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-8">
                <div className="text-center p-3 sm:p-4 rounded-xl border border-[#E2E8F0] bg-gradient-to-br from-white to-[#F8FAFC]">
                  <Music className="w-4 h-4 sm:w-5 sm:h-5 text-[#2563FF] mx-auto mb-1 sm:mb-2" />
                  <p className="font-mono text-lg sm:text-2xl text-[#0F172A]">{stats.tracks}</p>
                  <p className="font-mono text-[9px] sm:text-xs text-[#94A3B8] uppercase">Tracks</p>
                </div>
                <div className="text-center p-3 sm:p-4 rounded-xl border border-[#E2E8F0] bg-gradient-to-br from-white to-[#F8FAFC]">
                  <ListMusic className="w-4 h-4 sm:w-5 sm:h-5 text-[#2563FF] mx-auto mb-1 sm:mb-2" />
                  <p className="font-mono text-lg sm:text-2xl text-[#0F172A]">{stats.playlists}</p>
                  <p className="font-mono text-[9px] sm:text-xs text-[#94A3B8] uppercase">Playlists</p>
                </div>
                <div className="text-center p-3 sm:p-4 rounded-xl border border-[#E2E8F0] bg-gradient-to-br from-white to-[#F8FAFC]">
                  <FolderOpen className="w-4 h-4 sm:w-5 sm:h-5 text-[#2563FF] mx-auto mb-1 sm:mb-2" />
                  <p className="font-mono text-lg sm:text-2xl text-[#0F172A]">{stats.crates}</p>
                  <p className="font-mono text-[9px] sm:text-xs text-[#94A3B8] uppercase">Crates</p>
                </div>
              </div>
            )}
          </div>

          {/* Edit Profile Form */}
          <div className="glass-pane p-6 sm:p-8 mb-6">
            <h3 className="font-heading text-base sm:text-lg text-[#0F172A] mb-6">EDIT PROFILE</h3>
            
            <div className="space-y-5">
              <div>
                <label className="block font-mono text-xs text-[#64748B] mb-2 uppercase">
                  Display Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your DJ name"
                    className="pl-10 bg-white border-[#E2E8F0] text-[#0F172A]"
                    data-testid="profile-name"
                  />
                </div>
              </div>

              <div>
                <label className="block font-mono text-xs text-[#64748B] mb-2 uppercase">
                  Email (read-only)
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                  <Input
                    type="email"
                    value={user.email}
                    disabled
                    className="pl-10 bg-white/50 border-[#E2E8F0] text-[#64748B]"
                  />
                </div>
              </div>

              <div>
                <label className="block font-mono text-xs text-[#64748B] mb-2 uppercase">
                  Avatar URL
                </label>
                <Input
                  type="url"
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  placeholder="https://example.com/avatar.jpg"
                  className="bg-white border-[#E2E8F0] text-[#0F172A]"
                  data-testid="profile-avatar"
                />
              </div>

              <Button
                onClick={handleSave}
                disabled={loading}
                className="btn-primary"
                data-testid="profile-save"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Admin Link */}
          {isAdmin && (
            <div className="glass-pane p-6 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Shield className="w-6 h-6 text-[#2563FF] flex-shrink-0" />
                  <div>
                    <p className="font-heading text-sm sm:text-base text-[#0F172A]">Admin Dashboard</p>
                    <p className="font-body text-xs sm:text-sm text-[#64748B]">Manage users and view activity</p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/admin')}
                  variant="outline"
                  className="border-[#E2E8F0] w-full sm:w-auto"
                  data-testid="admin-link"
                >
                  Open Dashboard
                </Button>
              </div>
            </div>
          )}

          {/* My Tickets */}
          <div className="glass-pane p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-4 min-w-0">
                <LifeBuoy className="w-6 h-6 text-[#2563FF] flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-heading text-sm sm:text-base text-[#0F172A]">My Support Tickets</p>
                  <p className="font-body text-xs sm:text-sm text-[#64748B]">View past conversations and replies</p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/profile/tickets')}
                variant="outline"
                className="border-[#E2E8F0] w-full sm:w-auto"
                data-testid="my-tickets-link"
              >
                View Tickets
              </Button>
            </div>
          </div>

          {/* Coach Preferences — admin doesn't need personal prefs; show only to regular users */}
          {!isAdmin && (
            <div className="mb-6 mt-6">
              <CoachPrefsCard />
            </div>
          )}

          {/* Dashboard Widgets — pick which cards appear on the Home dashboard */}
          <DashboardWidgetsCard />

          {/* Desktop analyser — download + subscription-gated feature notes */}
          <DesktopAppCard />

          {/* Library Duplicates — surface tracks that look like duplicates */}
          <LibraryDuplicatesCard />

          {/* Missing Covers — bulk autofetch artwork from iTunes */}
          <MissingCoversCard />

          {/* Logout */}
          <div className="glass-pane p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="font-heading text-sm sm:text-base text-[#0F172A]">Sign Out</p>
                <p className="font-body text-xs sm:text-sm text-[#64748B]">Log out of your account</p>
              </div>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-[#E2E8F0] text-[#2563FF] hover:text-[#3B82F6] w-full sm:w-auto"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

/* ───────────────────── Dashboard widget settings ───────────────────── */

function DashboardWidgetsCard() {
  const { allWidgets, isEnabled, toggle, setEnabled, reset } = useDashboardWidgets();
  const enabledCount = allWidgets.filter((w) => isEnabled(w.id)).length;

  const SIZE_LABEL = { sm: 'Small', md: 'Medium', lg: 'Large', xl: 'X-Large', full: 'Full-width' };

  // Curated presets — applied with a single click.
  const PRESETS = [
    {
      id: 'minimalist',
      label: 'Minimalist',
      description: 'KPIs + Latest Set only',
      ids: ['kpi_tracks', 'kpi_sets', 'kpi_keymatch', 'latest_ai_set'],
    },
    {
      id: 'analyst',
      label: 'Analyst',
      description: 'Everything turned on',
      ids: allWidgets.map((w) => w.id),
    },
    {
      id: 'performer',
      label: 'Performer',
      description: 'Latest Set + Transition · KPIs hidden',
      ids: ['latest_ai_set', 'transition_card', 'energy_flow'],
    },
  ];

  const applyPreset = (preset) => {
    const want = new Set(preset.ids);
    for (const w of allWidgets) setEnabled(w.id, want.has(w.id));
  };

  return (
    <div className="glass-pane p-6 mb-6" data-testid="dashboard-widgets-card">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center flex-shrink-0">
            <LayoutGrid className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="font-heading text-sm sm:text-base font-semibold text-[#0F172A]">Dashboard widgets</p>
            <p className="font-body text-xs sm:text-sm text-[#64748B]">
              Toggle individual cards on or off. Layout auto-reflows so widgets never get squashed.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="font-mono text-[10px] uppercase tracking-wider text-[#64748B]">
            {enabledCount}/{allWidgets.length} on
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={reset}
            className="border-[#E2E8F0] text-[#64748B] hover:text-[#2563FF]"
            data-testid="dashboard-widgets-reset"
            title="Reset all to default"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1" />
            Reset
          </Button>
        </div>
      </div>

      {/* Quick preset row */}
      <div className="mb-5">
        <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#64748B] mb-2">
          Quick Layouts
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => applyPreset(p)}
              className="text-left p-3 rounded-xl border border-[#E2E8F0] bg-white/60 hover:border-[#2563FF] hover:bg-[#EFF6FF]/60 transition-all"
              data-testid={`dashboard-preset-${p.id}`}
              title={`Apply ${p.label} preset`}
            >
              <p className="font-heading text-sm font-semibold text-[#0F172A]">{p.label}</p>
              <p className="text-[11px] text-[#64748B] leading-snug mt-0.5">{p.description}</p>
              <p className="font-mono text-[9px] uppercase tracking-wider text-[#94A3B8] mt-1">
                {p.ids.length} widget{p.ids.length === 1 ? '' : 's'}
              </p>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {allWidgets.map((w) => {
          const on = isEnabled(w.id);
          return (
            <label
              key={w.id}
              className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                on
                  ? 'bg-[#EFF6FF]/40 border-[#DBEAFE] hover:border-[#2563FF]'
                  : 'bg-white/60 border-[#E2E8F0] hover:border-[#94A3B8]'
              }`}
              data-testid={`widget-toggle-${w.id}`}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggle(w.id)}
                className="mt-0.5 h-4 w-4 rounded border-[#CBD5E1] text-[#2563FF] focus:ring-[#2563FF]"
                data-testid={`widget-toggle-input-${w.id}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className={`font-heading text-sm font-semibold leading-tight ${on ? 'text-[#0F172A]' : 'text-[#64748B]'}`}>
                    {w.label}
                  </p>
                  <span className="font-mono text-[9px] uppercase tracking-wider text-[#94A3B8] flex-shrink-0">
                    {SIZE_LABEL[w.size] || w.size}
                  </span>
                </div>
                <p className="text-[11px] text-[#64748B] leading-snug mt-0.5">{w.description}</p>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

/* ───────────────────── Library Duplicates ───────────────────── */

function DuplicateWhyButton({ reason, groupKey }) {
  const [open, setOpen] = useState(false);

  // Close on outside click / Esc
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (!e.target.closest(`[data-why-popover="${groupKey}"]`)
          && !e.target.closest(`[data-why-trigger="${groupKey}"]`)) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, groupKey]);

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((s) => !s); }}
        className="w-5 h-5 rounded-full text-[#94A3B8] hover:text-[#2563FF] hover:bg-[#EFF6FF] flex items-center justify-center transition-colors flex-shrink-0"
        title="Why was this flagged?"
        data-why-trigger={groupKey}
        data-testid={`dup-why-${groupKey.slice(0, 20)}`}
        aria-label="Why was this flagged as a duplicate?"
        aria-expanded={open}
      >
        <HelpCircle className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          className="absolute z-30 left-0 top-6 w-72 max-w-[80vw] p-3 rounded-xl border border-[#E2E8F0] bg-white shadow-[0_8px_24px_-8px_rgba(15,23,42,0.18)]"
          data-why-popover={groupKey}
          role="dialog"
          data-testid={`dup-why-popover-${groupKey.slice(0, 20)}`}
        >
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#2563FF] mb-1">
            Why flagged
          </p>
          <p className="font-body text-[11px] text-[#475569] leading-relaxed whitespace-pre-wrap break-words">
            {reason || 'Same normalized title across multiple library entries.'}
          </p>
          <p className="font-body text-[10px] text-[#94A3B8] mt-2 italic">
            Untick any track that&apos;s genuinely a different song before removing.
          </p>
        </div>
      )}
    </span>
  );
}

function LibraryDuplicatesCard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [working, setWorking] = useState(null);
  // Map of group key → Set of selected (to-delete) track ids
  const [selections, setSelections] = useState({});
  const [autoMerge, setAutoMerge] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/library/duplicates`);
      setData(r.data);
      // Pre-select all *non-keepers* in each group (everything but item[0])
      const next = {};
      for (const g of (r.data.duplicate_groups || [])) {
        next[g.key] = new Set(g.tracks.slice(1).map((t) => t.id));
      }
      setSelections(next);
    } catch (e) {
      toast.error('Failed to load duplicate scan');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (groupKey, trackId) => {
    setSelections((prev) => {
      const set = new Set(prev[groupKey] || []);
      if (set.has(trackId)) set.delete(trackId); else set.add(trackId);
      return { ...prev, [groupKey]: set };
    });
  };

  const totalSelected = Object.values(selections).reduce((sum, s) => sum + s.size, 0);

  const buildGroupsPayload = (groupFilter) => {
    if (!data) return [];
    return (data.duplicate_groups || [])
      .filter((g) => !groupFilter || groupFilter === g.key)
      .map((g) => {
        const sel = selections[g.key] || new Set();
        const removeIds = Array.from(sel);
        if (removeIds.length === 0) return null;
        // Keeper = first non-selected track (or items[0] if all selected)
        const keeper = g.tracks.find((t) => !sel.has(t.id)) || g.tracks[0];
        return { keeper_id: keeper?.id, remove_ids: removeIds };
      })
      .filter(Boolean);
  };

  const removeSelected = async () => {
    if (totalSelected === 0) return;
    setWorking('all');
    try {
      const r = await axios.post(`${API}/library/duplicates/remove`, {
        groups: buildGroupsPayload(),
        auto_merge: autoMerge,
      });
      const merged = (r.data.crates_touched || 0) + (r.data.playlists_touched || 0);
      toast.success(
        `Removed ${r.data.deleted} duplicate${r.data.deleted === 1 ? '' : 's'}`
        + (autoMerge && merged > 0
          ? ` · merged ${r.data.crates_touched} crate${r.data.crates_touched === 1 ? '' : 's'} + ${r.data.playlists_touched} playlist${r.data.playlists_touched === 1 ? '' : 's'}`
          : '')
      );
      await load();
    } catch (e) {
      toast.error('Failed to remove duplicates');
    } finally {
      setWorking(null);
    }
  };

  const removeGroup = async (groupKey) => {
    const groups = buildGroupsPayload(groupKey);
    if (groups.length === 0) return;
    setWorking(groupKey);
    try {
      const r = await axios.post(`${API}/library/duplicates/remove`, {
        groups,
        auto_merge: autoMerge,
      });
      const merged = (r.data.crates_touched || 0) + (r.data.playlists_touched || 0);
      toast.success(
        `Removed ${r.data.deleted} from "${groupKey}"`
        + (autoMerge && merged > 0 ? ` · re-linked ${merged} membership${merged === 1 ? '' : 's'}` : '')
      );
      await load();
    } catch (e) {
      toast.error('Failed to remove duplicates from group');
    } finally {
      setWorking(null);
    }
  };

  return (
    <div className="glass-pane p-6 mb-6" data-testid="library-duplicates-card">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center flex-shrink-0">
            <Copy className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="font-heading text-sm sm:text-base font-semibold text-[#0F172A]">Library duplicates</p>
            <p className="font-body text-xs sm:text-sm text-[#64748B]">
              Smart guess at which tracks are the same song. Each group has a
              <span className="font-semibold"> confidence rating</span> — pick
              which copies to keep.
            </p>
          </div>
        </div>
        {data && (
          <span className="font-mono text-[10px] uppercase tracking-wider text-[#64748B] flex-shrink-0 whitespace-nowrap">
            {data.duplicate_count} extra{data.duplicate_count === 1 ? '' : 's'}
            {' / '}{data.library_size} tracks
          </span>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-[#64748B] py-6 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" />
          Scanning library…
        </div>
      )}

      {!loading && data && data.duplicate_groups.length === 0 && (
        <div className="flex items-center gap-2 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">No duplicates detected — your library is clean.</span>
        </div>
      )}

      {!loading && data && data.duplicate_groups.length > 0 && (
        <>
          <div className="space-y-3 mb-4">
            {data.duplicate_groups.map((g) => {
              const sel = selections[g.key] || new Set();
              return (
                <div
                  key={g.key}
                  className="rounded-xl border border-[#E2E8F0] bg-white/60 backdrop-blur p-3"
                  data-testid={`dup-group-${g.key.slice(0, 20)}`}
                >
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className="font-heading text-sm font-semibold text-[#0F172A] truncate">
                        {g.title_display}
                      </p>
                      <DuplicateWhyButton reason={g.reason} groupKey={g.key} />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeGroup(g.key)}
                      disabled={sel.size === 0 || working !== null}
                      className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                      data-testid={`dup-group-remove-${g.key.slice(0, 20)}`}
                    >
                      {working === g.key ? (
                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                      ) : (
                        <Trash2 className="w-3 h-3 mr-1" />
                      )}
                      Remove {sel.size}
                    </Button>
                  </div>
                  <div className="space-y-1.5">
                    {g.tracks.map((t, idx) => {
                      const isKeeper = idx === 0;
                      const isSelected = sel.has(t.id);
                      return (
                        <label
                          key={t.id}
                          className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-red-50/70 border-red-200'
                              : 'bg-white/80 border-[#E2E8F0] hover:border-[#94A3B8]'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(g.key, t.id)}
                            className="h-4 w-4 rounded border-[#CBD5E1] text-red-600 focus:ring-red-500"
                            data-testid={`dup-track-checkbox-${t.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-body text-sm text-[#0F172A] truncate">{t.title}</p>
                              {isKeeper && (
                                <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200 font-mono text-[9px] uppercase tracking-wider flex-shrink-0">
                                  Keep
                                </span>
                              )}
                            </div>
                            <p className="font-mono text-[11px] text-[#64748B] truncate">
                              {t.artist || '—'}
                              {t.bpm ? ` · ${Math.round(t.bpm)} BPM` : ''}
                              {t.camelot ? ` · ${t.camelot}` : ''}
                              {t.has_audio ? '' : ' · no audio'}
                            </p>
                          </div>
                          {isSelected && (
                            <AlertTriangle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t border-[#E2E8F0]">
            <div className="flex flex-col gap-1">
              <p className="font-mono text-[11px] text-[#64748B]">
                {totalSelected} selected for removal
              </p>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={autoMerge}
                  onChange={(e) => setAutoMerge(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-[#CBD5E1] text-[#2563FF] focus:ring-[#2563FF]"
                  data-testid="dup-auto-merge"
                />
                <span className="text-[11px] text-[#475569]">
                  Auto-merge crate & playlist memberships onto the keeper before removal
                </span>
              </label>
            </div>
            <Button
              onClick={removeSelected}
              disabled={totalSelected === 0 || working !== null}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="dup-remove-all"
            >
              {working === 'all' ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Remove all {totalSelected} selected
            </Button>
          </div>
        </>
      )}
    </div>
  );
}


/* ───────────────────── Missing Covers — bulk iTunes backfill ───────────────────── */

function MissingCoversCard() {
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState([]);
  const [running, setRunning] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [progress, setProgress] = useState({ done: 0, ok: 0, fail: 0, current: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tracks/missing-covers`);
      setMissing(r.data.missing || []);
    } catch (e) {
      toast.error('Failed to scan missing covers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAutofetch = async () => {
    if (missing.length === 0) return;
    setRunning(true);
    setCancelled(false);
    setProgress({ done: 0, ok: 0, fail: 0, current: null });

    let cancelFlag = false;
    const cancelHandler = () => { cancelFlag = true; setCancelled(true); };
    // Expose cancel via local closure
    runAutofetch._cancel = cancelHandler;

    let ok = 0;
    let fail = 0;
    let done = 0;
    for (const t of missing) {
      if (cancelFlag) break;
      setProgress({ done, ok, fail, current: `${t.title || ''}${t.artist ? ' — ' + t.artist : ''}` });
      try {
        const r = await axios.post(`${API}/tracks/${t.id}/cover/backfill`);
        if (r.data?.success) ok += 1; else fail += 1;
      } catch (e) {
        fail += 1;
      }
      done += 1;
      setProgress({ done, ok, fail, current: `${t.title || ''}${t.artist ? ' — ' + t.artist : ''}` });
      // tiny delay to avoid hammering iTunes
      await new Promise((r) => setTimeout(r, 120));
    }

    setRunning(false);
    setProgress((p) => ({ ...p, current: null }));
    if (cancelFlag) {
      toast.message(`Cancelled — fetched ${ok}, failed ${fail}`);
    } else {
      toast.success(`Covers fetched: ${ok} ok · ${fail} failed`);
    }
    await load();
  };

  const cancel = () => {
    if (typeof runAutofetch._cancel === 'function') runAutofetch._cancel();
  };

  const total = missing.length;
  const pct = total ? Math.round((progress.done / total) * 100) : 0;

  return (
    <div className="glass-pane p-6 mb-6" data-testid="missing-covers-card">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#22D3EE] to-[#2563FF] flex items-center justify-center flex-shrink-0">
            <ImageIcon className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="font-heading text-sm sm:text-base font-semibold text-[#0F172A]">Missing cover art</p>
            <p className="font-body text-xs sm:text-sm text-[#64748B]">
              Auto-fetch artwork for every track missing a cover from the
              <span className="font-semibold"> public iTunes catalog</span>.
              Matches on artist + title. Runs one track at a time with live progress.
            </p>
          </div>
        </div>
        {!loading && (
          <span className="font-mono text-[10px] uppercase tracking-wider text-[#64748B] flex-shrink-0 whitespace-nowrap" data-testid="missing-covers-count">
            {total} missing
          </span>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-[#64748B] py-6 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" />
          Scanning library…
        </div>
      )}

      {!loading && total === 0 && (
        <div className="flex items-center gap-2 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">All tracks have cover art — nothing to do.</span>
        </div>
      )}

      {!loading && total > 0 && (
        <>
          {/* Progress bar (shown while running, or after completion) */}
          {(running || progress.done > 0) && (
            <div className="mb-4" data-testid="missing-covers-progress">
              <div className="flex items-center justify-between text-xs font-mono text-[#64748B] mb-1.5">
                <span>{progress.done} / {total} · {pct}%</span>
                <span>
                  <span className="text-emerald-600">{progress.ok} ok</span>
                  {' · '}
                  <span className="text-red-500">{progress.fail} failed</span>
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-[#E2E8F0] overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#22D3EE] to-[#2563FF] transition-all duration-200"
                  style={{ width: `${pct}%` }}
                />
              </div>
              {progress.current && running && (
                <p className="mt-2 text-xs text-[#64748B] truncate">
                  <Loader2 className="w-3 h-3 inline-block animate-spin mr-1.5 -mt-0.5" />
                  {progress.current}
                </p>
              )}
              {cancelled && !running && (
                <p className="mt-2 text-xs text-amber-600">Cancelled.</p>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            {!running ? (
              <Button
                onClick={runAutofetch}
                className="bg-gradient-to-r from-[#2563FF] to-[#3B82F6] text-white hover:opacity-90"
                data-testid="missing-covers-autofetch-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Autofetch {total} cover{total === 1 ? '' : 's'}
              </Button>
            ) : (
              <Button
                onClick={cancel}
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50"
                data-testid="missing-covers-cancel-btn"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            )}
            <Button
              onClick={load}
              variant="outline"
              disabled={running}
              className="border-[#E2E8F0] text-[#64748B]"
              data-testid="missing-covers-refresh-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Rescan
            </Button>
          </div>
        </>
      )}
    </div>
  );
}



// ═════════════════════════════════════════════════════════════════════════════
// Desktop analyser card — download links + subscription-gated feature notes
// ═════════════════════════════════════════════════════════════════════════════
function DesktopAppCard() {
  const { isSubscribed, subscription } = useAuth();
  const [versions, setVersions]     = useState(null);
  const [downloading, setDownloading] = useState(null); // 'macos' | 'windows' | null

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/desktop/versions`)
      .then(r => { if (!cancelled) setVersions(r.data.platforms || {}); })
      .catch(() => { if (!cancelled) setVersions({}); });
    return () => { cancelled = true; };
  }, []);

  const download = async (platform) => {
    setDownloading(platform);
    try {
      const r = await axios.get(`${API}/desktop/download/${platform}`);
      // Trigger native download via anchor click — presigned URL has
      // Content-Disposition: attachment set server-side, so the browser
      // saves rather than navigates.
      const a = document.createElement('a');
      a.href = r.data.url;
      a.rel  = 'noopener';
      a.download = r.data.filename || '';
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Starting download of ${r.data.filename || 'installer'}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Download unavailable'));
    } finally {
      setDownloading(null);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const platforms = [
    { id: 'macos',   label: 'macOS',   Icon: Apple },
    { id: 'windows', label: 'Windows', Icon: Monitor },
  ];

  return (
    <div className="glass-pane p-6 mb-6" data-testid="desktop-app-card">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h3 className="font-heading text-base text-[#0F172A] flex items-center gap-2">
            <Download className="w-4 h-4 text-[#2563FF]" />
            Desktop Analyser
          </h3>
          <p className="font-body text-xs text-[#64748B] mt-1 max-w-lg">
            Run BPM/key/energy analysis locally on your machine — orders of magnitude faster than cloud analysis for big libraries. Login is free; analysis and cloud sync unlock with a Pro Monthly subscription.
          </p>
        </div>
        <div className="flex-shrink-0">
          {isSubscribed ? (
            <span className="text-[10px] font-heading bg-emerald-500 text-white px-2 py-1 rounded-full inline-flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Pro — Analysis unlocked
            </span>
          ) : (
            <span className="text-[10px] font-heading bg-amber-100 text-amber-700 border border-amber-200 px-2 py-1 rounded-full">
              Free — Read-only sync
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {platforms.map(({ id, label, Icon }) => {
          const info = versions?.[id];
          const available = info?.available;
          const isDownloading = downloading === id;
          return (
            <button
              key={id}
              disabled={!available || isDownloading || versions === null}
              onClick={() => download(id)}
              className={`text-left rounded-xl border p-4 transition-all group
                ${available
                  ? 'bg-white border-[#E2E8F0] hover:border-[#2563FF]/50 hover:shadow-[0_2px_10px_rgba(37,99,255,0.06)] cursor-pointer'
                  : 'bg-[#F5F7FB] border-[#E2E8F0] opacity-70 cursor-not-allowed'
                }`}
              data-testid={`desktop-download-${id}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  available ? 'bg-[#EFF6FF] text-[#2563FF]' : 'bg-[#E2E8F0] text-[#94A3B8]'
                }`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-sm text-[#0F172A]">Download for {label}</p>
                  <p className="font-mono text-[10px] text-[#94A3B8] mt-0.5">
                    {versions === null
                      ? 'Checking…'
                      : available
                        ? `v${info.version}  ·  ${formatSize(info.size_bytes)}`
                        : 'Not yet published'}
                  </p>
                  {isDownloading && (
                    <p className="font-mono text-[10px] text-[#2563FF] mt-1 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Preparing…
                    </p>
                  )}
                </div>
                {available && !isDownloading && (
                  <Download className="w-4 h-4 text-[#94A3B8] group-hover:text-[#2563FF] transition-colors" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {!isSubscribed && (
        <div className="mt-4 rounded-xl bg-gradient-to-r from-[#EFF6FF] to-transparent border border-[#2563FF]/20 p-3.5 flex items-center gap-3 flex-wrap">
          <Sparkles className="w-4 h-4 text-[#2563FF] flex-shrink-0" />
          <p className="font-body text-xs text-[#0F172A] flex-1 min-w-[200px]">
            Ready to analyse? Subscribe to <b>Pro Monthly ($9.99/mo)</b> to unlock local analysis, cloud sync, and 200 credits/month for web features.
          </p>
          <a
            href="/billing"
            className="text-xs font-heading bg-[#2563FF] text-white px-3 py-1.5 rounded-lg hover:bg-[#1D4ED8] transition-colors inline-flex items-center gap-1.5"
            data-testid="desktop-card-subscribe-cta"
          >
            <Zap className="w-3 h-3" /> Subscribe
          </a>
        </div>
      )}

      {isSubscribed && subscription?.period_end && (
        <p className="font-mono text-[10px] text-[#94A3B8] mt-3 text-center">
          Desktop analysis unlocked · Renews {new Date(subscription.period_end).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
        </p>
      )}
    </div>
  );
}
